import { FoodsTextInput, FoodsTextInputProps } from "@foodpilot/foods";
import { ScoreImprovement } from "src/types";
import { useState, useEffect, useCallback, useRef } from "react";
import { parseLocalizedNumber, formatNumber, formatNumberByType } from "src/utils/formatting";
import { NumericalScoreValue } from "src/api/client-api/olapScores/olapScoresSchema";
import { Field } from "src/api/sure-api/ssq/useGetOneSsqVersion";

type scoreWithUnitValue = number | string | null;
type Score = {
    score: scoreWithUnitValue;
    unit: string;
    comparisonValues?: {
        currentValue: NumericalScoreValue;
        comparedTo: NumericalScoreValue;
        improvement: ScoreImprovement;
    };
};

type SSQTextInputProps = FoodsTextInputProps & {
    // score: Score;
    // tooltipText?: ReactNode;
    onCalculate?: (valueToUse: string) => void;
    /**
     * Enable number localization (formatting and parsing)
     */
    enableLocalization?: boolean;
    /**
     * The locale for formatting (e.g., "fr", "en")
     */
    locale?: string;
    /**
     * The field type (e.g., "text", "number")
     */
    type?: Field["type"];
    /**
     * The unit title from SSQ property (e.g., "decimal", "integer", "%", "kg")
     */
    unitTitle?: string;
    /**
     * Precision of the numeric input
     */
    precision?: number;
};
export const SSQTextInput = (props: SSQTextInputProps) => {
    const {
        // tooltipText,
        // score,
        onCalculate,
        enableLocalization = false,
        locale = "en",
        type,
        unitTitle,
        precision,

        // Props from FoodsTextInputProps that we override here
        ExtraInputProps = {},
        defaultOption,
        value: propsValue = "",
        onChange: propsOnChange,

        ...FoodsTextInputProps
    } = props;

    // Localization state management
    const [displayValue, setDisplayValue] = useState<string>("");
    const [isFocused, setIsFocused] = useState(false);
    const numericDraftValue = useRef<string | null | undefined>(undefined);

    // Check if this should be treated as a numeric field for localization
    const isNumeric = enableLocalization && type === "number";

    // Format the value for display when not focused
    useEffect(() => {
        if (!enableLocalization) {
            setDisplayValue(propsValue);
            return;
        }

        if (!isFocused && propsValue && isNumeric) {
            const formatted = formatNumberByType(propsValue, unitTitle, locale, precision ?? 20);
            setDisplayValue(formatted);
        } else if (!isFocused && propsValue && !isNumeric) {
            // For non-numeric fields, just show the value as-is
            setDisplayValue(propsValue);
        } else if (!isFocused && !propsValue) {
            setDisplayValue("");
        }
    }, [propsValue, unitTitle, locale, precision, isFocused, isNumeric, enableLocalization]);

    // When focused, show the value without grouping separators: they would jump around
    // under the caret while typing, and confuse the precision guards below.
    useEffect(() => {
        if (enableLocalization && isNumeric && isFocused && propsValue) {
            const editableValue = formatNumber(propsValue, locale, {
                maximumFractionDigits: precision ?? 20,
                useGrouping: false,
                withPrecisionFloor: false,
            });
            setDisplayValue(editableValue);
        }
    }, [isFocused, enableLocalization]);

    const handleChange = useCallback(
        (newValue: string) => {
            if (!enableLocalization) {
                propsOnChange?.(newValue);
                return;
            }

            setDisplayValue(newValue);

            if (!isNumeric) {
                // For non-numeric fields, pass through directly
                propsOnChange?.(newValue);
                return;
            }

            // Parse the localized input to standard format
            const standardValue = parseLocalizedNumber(newValue, locale);
            const isValidNumericDraft = newValue === "" || standardValue !== "";

            // Keep localized numeric edits internal until blur. This prevents invalid text from
            // clearing or dirtying the parent while still allowing an intentional empty value.
            numericDraftValue.current = isValidNumericDraft ? standardValue : null;
        },
        [locale, propsOnChange, isNumeric, enableLocalization],
    );

    const handleFocus = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            if (enableLocalization) {
                setIsFocused(true);
            }
            if (isNumeric) {
                numericDraftValue.current = undefined;
            }
            ExtraInputProps.onFocus?.(e);
        },
        [ExtraInputProps, enableLocalization, isNumeric],
    );

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            if (enableLocalization) {
                setIsFocused(false);
            }

            const relatedTarget = e.nativeEvent.relatedTarget as Element;
            const isTargetPopover = relatedTarget && relatedTarget.classList.contains("MuiPopover-paper");

            if (e.nativeEvent.type === "focusout" && isTargetPopover) {
                return;
            }

            if (enableLocalization && isNumeric) {
                const draftValue = numericDraftValue.current;
                numericDraftValue.current = undefined;

                if (draftValue === null) return;
                if (draftValue !== undefined) {
                    propsOnChange?.(draftValue);
                    onCalculate?.(draftValue);
                    return;
                }
            }

            // Use the original value for onCalculate (standardized format)
            onCalculate?.(enableLocalization ? propsValue : displayValue);
        },
        [ExtraInputProps, enableLocalization, isNumeric, onCalculate, propsOnChange, propsValue, displayValue],
    );

    // const currentValue = props.score.comparisonValues?.currentValue ?? null;
    // const comparedTo = props.score.comparisonValues?.comparedTo ?? null;

    // Determine which value to use
    const inputValue = enableLocalization ? displayValue : propsValue;
    const inputOnChange = enableLocalization ? handleChange : propsOnChange;
    return (
        <FoodsTextInput
            {...FoodsTextInputProps}
            value={inputValue}
            onChange={inputOnChange}
            defaultOption={
                defaultOption === undefined ? defaultOption : (
                    {
                        ...defaultOption,
                        extraAction: (defaultValue) => onCalculate?.(defaultValue),
                    }
                )
            }
            ExtraInputProps={{
                ...ExtraInputProps,
                // A localized number ("15 000") is not a valid <input type="number"> value:
                // the browser would display nothing at all. [PF-1246]
                type: isNumeric ? "text" : ExtraInputProps.type,
                onFocus: handleFocus,
                onBlur: handleBlur,
                inputProps: {
                    // MUI only forwards `inputProps` to the <input>: set on the InputBase itself,
                    // `inputMode` would sit on the wrapper and mobile would lose the digit keypad.
                    inputMode: isNumeric ? "decimal" : ExtraInputProps.inputMode,
                    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (precision === 0 && /[.,]/.test(e.key)) {
                            e.preventDefault();
                        }
                    },
                    onInput: (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                        if (!isNumeric || precision === undefined) return;

                        const input = e.currentTarget;
                        const value = input.value;
                        const separatorIndex = Math.max(value.indexOf("."), value.indexOf(","));

                        if (separatorIndex !== -1) {
                            if (precision === 0) {
                                input.value = value.slice(0, separatorIndex);

                                return;
                            }

                            input.value = value.slice(0, separatorIndex + 1 + precision);
                        }
                    },
                },
            }}
        />
    );
};

// <Stack
//     flexDirection={{ xs: "column", sm: "row" }}
//     gap={{ xs: theme.spacing(2), sx: theme.spacing(1) }}
//     alignItems={{ xs: "left", sm: "center" }}
//     justifyContent={"space-between"}
//     width={"100%"}
// >
//     <FormTooltip title={tooltipText} arrow placement="right">
//         <Box>

{
    /* </Box>
            </FormTooltip>
            {score.score !== null && (
                <Stack flexDirection={"row"} gap={theme.spacing(1)}>
                    <FormUnit
                        value={score.score}
                        unit={score.unit}
                        comparison={
                            props.score.comparisonValues !== undefined ?
                                {
                                    value: currentValue,
                                    toCompareTo: comparedTo,
                                    improvement: props.score.comparisonValues.improvement,
                                }
                            :   undefined
                        }
                    />
                </Stack>
            )}
        </Stack> */
}
